import React, { useEffect, useRef, useState } from 'react'
import { useInView } from './Reveal'

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/** Counts a stat up from zero once it enters view. Suffix/prefix are preserved. */
export const CountUp = ({ value, duration = 1600, className = '' }) => {
  const [ref, inView] = useInView()

  // Split "23+" into 23 and "+" so any suffix survives the animation.
  const target = parseFloat(String(value).replace(/[^\d.]/g, '')) || 0
  const suffix = String(value).replace(/[\d.,]/g, '')

  // Rest at the real number. The animation drops to zero and climbs back only
  // once we know rAF is running, so a throttled or failed frame loop can never
  // leave a headline stat reading "0".
  const [display, setDisplay] = useState(target)

  useEffect(() => {
    if (!inView || reducedMotion()) return

    let raf = null
    let start = null
    const tick = (now) => {
      if (start === null) start = now
      const t = Math.min((now - start) / duration, 1)
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t) // easeOutExpo
      setDisplay(Math.round(target * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    // If frames never arrive, snap to the real value rather than sit at zero.
    const failsafe = setTimeout(() => setDisplay(target), duration + 600)

    return () => {
      if (raf !== null) cancelAnimationFrame(raf)
      clearTimeout(failsafe)
    }
  }, [inView, target, duration])

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString()}
      {suffix}
    </span>
  )
}

/**
 * Infinite horizontal ticker. Children are rendered twice so the track can
 * translate -50% and loop seamlessly.
 */
export const Marquee = ({ children, speed = 'normal', reverse = false, scrollable = false, className = '' }) => (
  <div className={`marquee ${scrollable ? 'overflow-x-auto overflow-y-hidden touch-pan-x' : 'overflow-hidden'} ${className}`}>
    <div
      className={`marquee-track ${
        reverse
          ? speed === 'fast'
            ? 'animate-marquee-reverse-fast'
            : 'animate-marquee-reverse'
          : speed === 'fast'
            ? 'animate-marquee-fast'
            : 'animate-marquee'
      }`}
    >
      <div className="flex shrink-0 items-center">{children}</div>
      <div className="flex shrink-0 items-center" aria-hidden="true">
        {children}
      </div>
    </div>
  </div>
)

/** Thin reading-progress bar pinned to the top of the window. */
export const ScrollProgress = () => {
  const [pct, setPct] = useState(0)

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement
      const max = doc.scrollHeight - doc.clientHeight
      setPct(max > 0 ? (doc.scrollTop / max) * 100 : 0)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-0.5">
      <div
        className="h-full bg-accent-500 transition-[width] duration-150 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

/** Translates its children slightly against the scroll for depth. */
export const Parallax = ({ children, strength = 40, className = '' }) => {
  const ref = useRef(null)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (reducedMotion()) return
    const node = ref.current
    if (!node) return

    let raf = null
    const update = () => {
      raf = null
      const rect = node.getBoundingClientRect()
      const vh = window.innerHeight
      if (rect.bottom < -200 || rect.top > vh + 200) return
      // -1 .. 1 across the viewport
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2)
      setOffset(progress * -strength)
    }
    const onScroll = () => {
      if (raf === null) raf = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      if (raf !== null) cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [strength])

  return (
    <div ref={ref} className={className}>
      <div style={{ transform: `translate3d(0, ${offset}px, 0)`, willChange: 'transform' }}>
        {children}
      </div>
    </div>
  )
}
